<template>
    <div class="form-group">
        <label class="headline-section pb-3 pt-3"
            >{{ $t("Username") }}: <b>{{ newUsername }}</b>
        </label>
        <input
            type="text"
            name="profile-name"
            autocomplete="off"
            @input="markDirty"
            v-model="temporalNewUsername"
            :placeholder="placeholderNewUsername"
            class="form-control"
            maxlength="255"
        />
    </div>
    <div class="form-group mt-3">
        <label class="headline-section pb-3 pt-3">{{ $t("Enter the password to save the changes") }}</label>
        <input type="password" name="confirm-pass" autocomplete="off" v-model="password" class="form-control" maxlength="255" />
    </div>
    <div v-if="error" class="error-message">{{ error }}</div>
    <div class="btn-bottom-modal d-flex justify-content-end align-items-center mt-3">
        <button type="button" class="btn btn-primary btn-mr" @click="updateUsername">
            <IconItem icon="mdi:rotate-right" /><span class="text-responsive">{{ $t("Update account") }}</span>
        </button>
    </div>
</template>

<script lang="ts">
import { AuthController } from "@/control/auth";
import { Timeouts } from "@/utils/timeout";
import { getUniqueStringId } from "@/utils/unique-id";
import { useVModel } from "@/utils/v-model";
import { defineComponent } from "vue";
import { Request } from "@asanrom/request-browser";
import { ApiAccount } from "@/api/api-group-account";

export default defineComponent({
    name: "UsernamePage",
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
            saveRequestId: getUniqueStringId(),
        };
    },
    data: function () {
        return {
            newUsername: AuthController.Username,
            temporalNewUsername: "",
            tfa: AuthController.RequiresTwoFactorAuthentication,
            token: "",
            error: "",
            busy: false,
            password: "",
            placeholderNewUsername: this.$t("New username"),
            dirty: false,
        };
    },
    methods: {
        onAuthChanged: function () {
            this.newUsername = AuthController.Username;
            this.tfa = AuthController.RequiresTwoFactorAuthentication;
            if (!AuthController.isAuthenticated()) {
                this.$requireLogin();
            }
        },

        reset: function () {
            this.error = "";
            this.temporalNewUsername = "";
            this.password = "";
        },

        updateUsername: function () {
            if (this.busy) {
                return;
            }

            this.error = "";
            this.busy = true;

            Request.Pending(
                this.saveRequestId,
                ApiAccount.ChangeUsername({ username: this.temporalNewUsername, password: this.password, tfa_token: this.token }),
            )
                .onSuccess(() => {
                    this.busy = false;
                    this.token = "";
                    this.dirty = false;
                    this.$showSnackBar(this.$t("Username updated successfully"));
                    AuthController.CheckAuthStatus();
                    this.reset();
                })
                .onCancel(() => {
                    this.busy = false;
                })
                .onRequestError((err, handleErr) => {
                    this.busy = false;
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        badRequestCharacterInvalid: () => {
                            this.error = this.$t("Invalid username provided. You have used an invalid character.");
                        },
                        badRequestUsernameMin3Max20: () => {
                            this.error = this.$t("Invalid username provided. Username must be between 3 and 20 characters in length.");
                        },
                        badRequestInvalidTfaCode: () => {
                            this.error = this.$t("Invalid one-time password");
                        },
                        badRequestUsernameInvalid: () => {
                            this.error = this.$t("Invalid username provided");
                        },
                        badRequestUsernameInUse: () => {
                            this.error = this.$t("The provided username is already in use");
                        },
                        badRequestWrongPassword: () => {
                            this.error = this.$t("Wrong password");
                        },
                        badRequest: () => {
                            this.error = this.$t("Bad request");
                        },
                        serverError: () => {
                            this.error = this.$t("Internal server error");
                        },
                        networkError: () => {
                            this.error = this.$t("Could not connect to the server");
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    this.error = err.message;
                    console.error(err);
                    this.busy = false;
                });
        },

        markDirty: function () {
            this.dirty = true;
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-changed", this.onAuthChanged.bind(this));
    },
    beforeUnmount: function () {
        Timeouts.Abort(this.saveRequestId);
        Request.Abort(this.saveRequestId);
    },
});
</script>

<style scoped>
@import "@/style/routes/configuration/user-config.css";
</style>
